import React from 'react';
import { cn } from '@/utils/merge';
import { termColor } from './autoAssignLabels';
import { pickBoardWinner } from './scoreBoardRows';

// The scoring board: one card per rider, showing what each criterion gave them
// and who therefore wins.
//
// Used in two places on purpose. Next to the weight sliders it runs on two
// invented riders, so dragging a slider flips a winner immediately without
// scrolling anywhere. On a timeline decision it runs on the riders who were
// actually considered at that moment. Same component, so the thing you learn
// from the sliders is the thing you read on a real decision.
//
// The rows themselves are built in scoreBoardRows.js — see there for what gets
// a row and why.

// `winnerId` is the rider a REAL decision gave the order to. Pass it whenever
// one is on record — see pickBoardWinner in scoreBoardRows.js for why the
// scores alone are not enough, and for the checks on it.
export const ScoreBoard = ({ rows, winnerId, columnsClassName = 'sm:grid-cols-2' }) => {
	const { best, tied } = pickBoardWinner({ rows, winnerId });

	return (
		<div className='flex flex-col gap-y-2'>
			<div className={cn('grid grid-cols-1 gap-2', columnsClassName)}>
				{rows.map(row => {
					const wins = !row.excluded && !tied && best && row.id === best.id;
					return (
						<div
							key={row.id}
							className={cn(
								'flex flex-col rounded-md border p-3',
								row.excluded
									? 'border-red-200 bg-red-50/40'
									: wins
										? 'border-emerald-400 bg-white shadow-sm'
										: 'border-slate-200 bg-white',
							)}
						>
							<div className='flex flex-row items-start justify-between gap-x-2'>
								<div className='flex min-w-0 flex-col'>
									<span className='text-[15px]/[21px] font-semibold text-slate-700'>
										{row.name}
									</span>
									{row.summary && (
										<span className='text-[13px]/[17px] text-slate-500'>{row.summary}</span>
									)}
								</div>
								<span
									className={cn(
										'shrink-0 rounded px-2 py-0.5 font-mono text-lg font-semibold',
										row.excluded
											? 'bg-red-100 text-[14px] text-red-700'
											: wins
												? 'bg-emerald-50 text-emerald-700'
												: 'bg-slate-100 text-slate-500',
									)}
								>
									{row.excluded ? 'Εκτός' : row.score}
								</span>
							</div>

							{row.excluded && (
								<div className='mt-2 text-[13px]/[17px] font-semibold text-red-700'>
									{row.excludedReason}
								</div>
							)}

							{!!row.details?.length && (
								<ul className='mt-2 flex flex-col gap-y-px'>
									{row.details.map(d => (
										<li key={d} className='text-[12px] text-slate-500'>
											• {d}
										</li>
									))}
								</ul>
							)}

							{row.staleNote && (
								<div className='mt-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500'>
									{row.staleNote}
								</div>
							)}

							{!!row.parts?.length && (
								<div className='mt-2 flex flex-col gap-y-0.5 border-t border-slate-100 pt-2'>
									{row.parts.map(p => (
										<div key={p.key} className='flex flex-col'>
											<div className='flex flex-row items-center gap-x-1.5 text-[12px]'>
												<span
													className={cn(
														'size-2 shrink-0 rounded-full',
														p.dropped ? 'bg-slate-300' : termColor(p.key).dot,
													)}
												/>
												<span
													className={cn(
														'min-w-0 flex-1 truncate',
														p.dropped ? 'text-slate-400 line-through' : termColor(p.key).text,
													)}
												>
													{p.label}
												</span>
												<span
													className={cn(
														'shrink-0 text-right font-mono',
														p.dropped
															? 'text-[11px] text-slate-400'
															: 'w-8 font-semibold text-slate-700',
													)}
												>
													{p.dropped ? 'δεν μέτρησε' : p.contribution}
												</span>
											</div>
											<div className='pl-3.5 text-[11px] text-slate-400'>
												{p.dropped ? p.basis : `${p.basis} → ${p.score} βαθμοί × ${p.share}%`}
											</div>
										</div>
									))}
									<div className='mt-0.5 flex flex-row justify-between border-t border-slate-100 pt-0.5 text-[12px]'>
										<span className='text-slate-500'>Σύνολο</span>
										<span className='font-mono font-semibold text-slate-700'>{row.score}</span>
									</div>
									{row.parts.some(p => p.fallback) && (
										<div className='text-[11px] text-slate-400'>
											Καμία βαρύτητα δεν ίσχυσε, οπότε αποφασίζει μόνος του ο χρόνος χωρίς
											παραγγελία.
										</div>
									)}
								</div>
							)}

							{/* The values that exclude rather than score, shown even when this
							    rider passed them — otherwise there is no way to tell whether a
							    limit you set is doing anything at all. */}
							{!!row.limits?.length && (
								<div className='mt-1.5 flex flex-col gap-y-px border-t border-slate-100 pt-1.5'>
									{row.limits.map(l => (
										<div key={l} className='text-[11px] text-slate-400'>
											{l}
										</div>
									))}
								</div>
							)}

							{wins && (
								<div className='mt-2 text-[13px]/[17px] font-semibold text-emerald-700'>
									Παίρνει την παραγγελία
								</div>
							)}
						</div>
					);
				})}
			</div>

			{!best && (
				<div className='text-[13px]/[17px] font-semibold text-red-700'>
					Κανένας δεν περνάει τα όρια, οπότε η παραγγελία μένει χωρίς διανομέα.
				</div>
			)}
		</div>
	);
};

export default ScoreBoard;
